import {
  getFocusedCommentContentId,
  useDatabaseContent,
} from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectAllVisibleUnresolvedThreads } from '@databases/utils/redux/comments/selectors';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, { createContext, useEffect, useMemo, useState } from 'react';

import { fetchParentThreadIdFromCommentContentId } from '../fetch-parent-comment';
import { type ThreadWithValueId } from '../type';

export type OpenThreadContextData = {
  valueId: string | undefined;
  threadId?: string | undefined;
  create?: boolean;
  panel?: boolean;
};

export interface CommentStateContextInterface {
  openThread: OpenThreadContextData;
  focusedCommentThreadId: string | undefined;
  allThreads: ThreadWithValueId[];
}

export const CommentStateContext = createContext<CommentStateContextInterface>({
  openThread: { valueId: undefined },
  focusedCommentThreadId: undefined,
  allThreads: [],
});

export interface CommentActionsContextInterface {
  setOpenThread: (value: OpenThreadContextData) => void;
  setFocusedCommentThreadId: (threadId: string | undefined) => void;
}

export const CommentActionsContext = createContext<CommentActionsContextInterface>({
  setOpenThread: () => {},
  setFocusedCommentThreadId: () => {},
});

export const CommentProvider = ({ children }: { children: React.ReactNode }) => {
  const [openThread, setOpenThread] = useState<OpenThreadContextData>({
    valueId: undefined,
  });
  const [focusedCommentThreadId, setFocusedCommentThreadId] = useState<
    string | undefined
  >(getFocusedCommentContentId());
  const { contentId } = useDatabaseContent();
  const store = defaultStoreUtils.useStore();

  useEffect(() => {
    if (!contentId) {
      return;
    }

    const focusedCommentContentIdFromParam = getFocusedCommentContentId();

    if (focusedCommentContentIdFromParam) {
      fetchParentThreadIdFromCommentContentId(
        contentId,
        focusedCommentContentIdFromParam
      ).then((threadId) => {
        setFocusedCommentThreadId(threadId);
      });
    }
  }, [contentId]);

  const commentThreads = defaultStoreUtils.useEqualSelector((state) => {
    if (!contentId) {
      return undefined;
    }
    const database = selectDatabaseByContentIdOptional(state, { contentId });

    if (!database || isBrokenDatabase(database)) {
      return undefined;
    }

    return database.commentThreads;
  });

  const allThreads = useMemo(() => {
    if (!contentId || !commentThreads) {
      return [];
    }
    return selectAllVisibleUnresolvedThreads(store.getState(), { contentId });
  }, [contentId, commentThreads, store]);

  const state = useMemo(
    () => ({
      openThread,
      focusedCommentThreadId,
      allThreads,
    }),
    [openThread, focusedCommentThreadId, allThreads]
  );

  const actions = useMemo(
    () => ({
      setOpenThread,
      setFocusedCommentThreadId,
    }),
    [setOpenThread, setFocusedCommentThreadId]
  );

  return (
    <CommentActionsContext.Provider value={actions}>
      <CommentStateContext.Provider value={state}>
        {children}
      </CommentStateContext.Provider>
    </CommentActionsContext.Provider>
  );
};
